import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { produce } from 'immer'
import { RecentItemType } from 'utils/storage'

import { ActionParamPlaceHolder } from '../model'
import { SearchState, SearchStateListItem, SearchStateListType } from '../model/search'

const initialState: SearchState = {
  autosuggestList: [],
  browseList: [],
  error: undefined,
  isLoading: false,
  lastUpdatedList: undefined,
  locationAddress: [],
  query: '',
  recentItemList: [],
  searchItemList: [],
}

const searchSlice = createSlice({
  initialState,
  name: 'search',
  reducers: {
    clearRecentItemList: {
      prepare: (flow: 'settings' | 'landing' | 'route') => {
        return { payload: { flow } }
      },
      reducer: (state: SearchState) => {
        return produce(state, (draft) => {
          draft.isLoading = true
        })
      },
    },
    fetchAutosuggestItemList: {
      prepare: (query: string, at?: H.geo.IPoint) => {
        return { payload: { at, query } }
      },
      reducer: (state: SearchState) => {
        return produce(state, (draft) => {
          draft.isLoading = true
        })
      },
    },
    fetchBrowseItemList: {
      prepare: (
        at: H.geo.IPoint | ActionParamPlaceHolder,
        categories: string[],
        limit?: number,
        name?: string | ActionParamPlaceHolder
      ) => {
        return { payload: { at, categories, limit, name } }
      },
      reducer: (state: SearchState) => {
        return produce(state, (draft) => {
          draft.isLoading = true
        })
      },
    },
    fetchLocationAddress: {
      prepare: (at?: H.geo.IPoint | ActionParamPlaceHolder, type?: string) => {
        return { payload: { at, type } }
      },
      reducer: (state: SearchState) => {
        return produce(state, (draft) => {
          draft.isLoading = true
        })
      },
    },
    fetchRecentItemList(state: SearchState) {
      return produce(state, (draft) => {
        draft.isLoading = true
      })
    },
    fetchSearchItemList: {
      prepare: (
        query: string | ActionParamPlaceHolder,
        at?: H.geo.IPoint | ActionParamPlaceHolder,
        searchIn?: string | ActionParamPlaceHolder
      ) => {
        return { payload: { at, query, searchIn } }
      },
      reducer: (state: SearchState) => {
        return produce(state, (draft) => {
          draft.isLoading = true
        })
      },
    },
    setAutosuggestItemList(state: SearchState, action: PayloadAction<SearchStateListItem[]>) {
      return produce(state, (draft) => {
        draft.autosuggestList = action.payload
        draft.isLoading = false
      })
    },
    setBrowseItemList(state: SearchState, action: PayloadAction<SearchStateListItem[]>) {
      return produce(state, (draft) => {
        draft.browseList = action.payload
        draft.isLoading = false
      })
    },
    setError(state: SearchState, action: PayloadAction<string>) {
      return produce(state, (draft) => {
        draft.error = action.payload
      })
    },
    setIsLoading(state: SearchState, action: PayloadAction<boolean>) {
      return produce(state, (draft) => {
        draft.isLoading = action.payload
      })
    },
    setLastUpdatedList(state: SearchState, action: PayloadAction<SearchStateListType | undefined>) {
      return produce(state, (draft) => {
        draft.lastUpdatedList = action.payload
      })
    },
    setQuery(state: SearchState, action: PayloadAction<string>) {
      return produce(state, (draft) => {
        draft.query = action.payload
      })
    },
    setRecentItem: {
      prepare: (item?: SearchStateListItem, query?: string, type?: RecentItemType) => {
        return { payload: { item, query, type } }
      },
      reducer: (state: SearchState) => {
        return produce(state, (draft) => {
          draft.isLoading = true
        })
      },
    },
    setRecentItemList(state: SearchState, action: PayloadAction<SearchStateListItem[]>) {
      return produce(state, (draft) => {
        draft.recentItemList = action.payload
      })
    },
    setReverseGeocodeItemList(state: SearchState, action: PayloadAction<SearchStateListItem[]>) {
      return produce(state, (draft) => {
        draft.locationAddress = action.payload
        draft.isLoading = false
      })
    },
    setSearchItemList(state: SearchState, action: PayloadAction<SearchStateListItem[]>) {
      return produce(state, (draft) => {
        draft.searchItemList = action.payload
        draft.isLoading = false
      })
    },
  },
})

export const {
  clearRecentItemList,
  fetchBrowseItemList,
  fetchSearchItemList,
  fetchAutosuggestItemList,
  fetchLocationAddress,
  setAutosuggestItemList,
  setSearchItemList,
  setReverseGeocodeItemList,
  setBrowseItemList,
  setLastUpdatedList,
  setError,
  setQuery,
  setRecentItem,
  fetchRecentItemList,
  setRecentItemList,
  setIsLoading,
} = searchSlice.actions
export const reducer = searchSlice.reducer
